import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusCircle, Plus, Trash2, DollarSign, Edit3, Check, X, Info } from 'lucide-react';
import { Toggle } from './Toggle';
import { useModifiers, Modifier } from './ModifiersContext';
export function ModifiersPanel() {
  const {
    modifiers,
    addModifier,
    updateModifier,
    deleteModifier
  } = useModifiers();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{
    name: string;
    price: string;
  }>({
    name: '',
    price: ''
  });
  const startEdit = (mod: Modifier) => {
    setEditingId(mod.id);
    setDraft({
      name: mod.name,
      price: mod.price.toString()
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setDraft({
      name: '',
      price: ''
    });
  };
  const saveEdit = () => {
    if (!editingId) return;
    const priceNum = parseFloat(draft.price);
    if (!draft.name.trim() || isNaN(priceNum) || priceNum < 0) return;
    updateModifier(editingId, {
      name: draft.name.trim(),
      price: priceNum
    });
    cancelEdit();
  };
  const handleAdd = () => {
    addModifier({
      name: 'New modifier',
      price: 0,
      active: true
    });
  };
  return <div className="flex flex-col gap-4" data-id="element-2664">
      {/* Info banner */}
      <div className="bg-purple-50/60 border border-purple-100 rounded-xl p-4 flex gap-3" data-id="element-2665">
        <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0" data-id="element-2666">
          <Info size={16} data-id="element-2667" />
        </div>
        <div className="flex-1" data-id="element-2668">
          <div className="text-sm font-semibold text-purple-900" data-id="element-2669">
            How modifiers work
          </div>
          <p className="text-xs text-purple-700/90 mt-1 leading-relaxed" data-id="element-2670">
            Define your reusable modifiers here once, then attach the relevant
            ones to each menu item from the item editor. Customers will see this
            list as optional add-ons when ordering. Set price to $0 for free
            options like sauces.
          </p>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-1" data-id="element-2671">
        <div data-id="element-2672">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-2673">Modifiers</h2>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-2674">
            {modifiers.length} modifier{modifiers.length === 1 ? '' : 's'} in
            your library
          </p>
        </div>
        <button onClick={handleAdd} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2.5 sm:py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 shadow-sm w-full sm:w-auto" data-id="element-2675">
          <Plus size={16} data-id="element-2676" />
          New Modifier
        </button>
      </div>

      {/* Modifier List */}
      <div className="flex flex-col gap-2.5" data-id="element-2677">
        <AnimatePresence mode="popLayout" data-id="element-2678">
          {modifiers.map(mod => {
          const isEditing = editingId === mod.id;
          return <motion.div key={mod.id} layout initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.18
          }} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4" data-id="element-2679">
                {isEditing ? <div className="flex flex-col sm:flex-row gap-2 sm:items-center" data-id="element-2680">
                    <input type="text" value={draft.name} onChange={e => setDraft({
                ...draft,
                name: e.target.value
              })} placeholder="Modifier name" autoFocus className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-2681" />
                    <div className="relative w-32" data-id="element-2682">
                      <DollarSign size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-2683" />
                      <input type="number" step="0.01" min="0" value={draft.price} onChange={e => setDraft({
                  ...draft,
                  price: e.target.value
                })} placeholder="0.00" className="w-full pl-8 pr-2 py-2 rounded-lg border border-gray-200 text-sm font-medium focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-2684" />
                    </div>
                    <div className="flex gap-2 sm:gap-1.5" data-id="element-2685">
                      <button onClick={cancelEdit} className="px-3 py-2 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors flex items-center gap-1" data-id="element-2686">
                        <X size={13} data-id="element-2687" /> Cancel
                      </button>
                      <button onClick={saveEdit} className="px-3 py-2 rounded-lg text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors shadow-sm flex items-center gap-1" data-id="element-2688">
                        <Check size={13} data-id="element-2689" /> Save
                      </button>
                    </div>
                  </div> : <div className="flex items-center gap-3" data-id="element-2690">
                    <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0" data-id="element-2691">
                      <PlusCircle size={16} data-id="element-2692" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-2693">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-2694">
                        <h3 className="font-semibold text-gray-900 text-sm" data-id="element-2695">
                          {mod.name}
                        </h3>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-bold tracking-wide ${mod.price === 0 ? 'bg-gray-100 text-gray-600' : 'bg-emerald-50 text-emerald-700'}`} data-id="element-2696">
                          {mod.price === 0 ? 'FREE' : `+$${mod.price.toFixed(2)}`}
                        </span>
                        {!mod.active && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-bold uppercase tracking-wider" data-id="element-2697">
                            Disabled
                          </span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-2698">
                      <Toggle checked={mod.active} onChange={v => updateModifier(mod.id, {
                  active: v
                })} data-id="element-2699" />
                      <button onClick={() => startEdit(mod)} className="text-gray-400 hover:text-orange-500 transition-colors p-1.5" aria-label="Edit modifier" data-id="element-2700">
                        <Edit3 size={15} data-id="element-2701" />
                      </button>
                      <button onClick={() => deleteModifier(mod.id)} className="text-gray-400 hover:text-red-500 transition-colors p-1.5" aria-label="Delete modifier" data-id="element-2702">
                        <Trash2 size={15} data-id="element-2703" />
                      </button>
                    </div>
                  </div>}
              </motion.div>;
        })}
        </AnimatePresence>

        {modifiers.length === 0 && <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 flex flex-col items-center justify-center text-center" data-id="element-2704">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3" data-id="element-2705">
              <PlusCircle size={22} className="text-gray-400" data-id="element-2706" />
            </div>
            <h3 className="font-semibold text-gray-900 text-sm mb-1" data-id="element-2707">
              No modifiers yet
            </h3>
            <p className="text-xs text-gray-500 max-w-xs" data-id="element-2708">
              Add your first modifier to start attaching add-ons to menu items.
            </p>
            <button onClick={handleAdd} className="mt-4 px-4 py-2 rounded-lg text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors shadow-sm flex items-center gap-1.5" data-id="element-2709">
              <Plus size={13} data-id="element-2710" />
              New Modifier
            </button>
          </div>}
      </div>
    </div>;
}